import type { Translate } from "@/lib/i18n/translate";
import { BRAND_LOGO_ALT, BRAND_LOGO_TITLE } from "@/lib/marketing-seo";
import { BrandLogoMark } from "@v1/ui/brand-logo-mark";
import type { LucideIcon } from "lucide-react";
import { Bot, Building2, Cable, Inbox, Receipt, Truck } from "lucide-react";
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactNode } from "react";

/**
 * Design tokens for the Pricing page — black and white on white, Inter
 * everywhere, matching the landing page (`hero/hero-tokens.ts`).
 *
 * `ink` carries everything loud: buttons, headlines, the big price. Verdigris
 * survives only as a small marker — the "hausverwalter.ai" side of the tool
 * comparison, "inklusive" tags, changed values, an open info icon — and as
 * `mint` on the dark calculator and popover surfaces.
 */
const INTER_STACK =
  "var(--font-inter), system-ui, -apple-system, 'Segoe UI', sans-serif";

export const T = {
  ink: "#0B0F19",
  accent: "#008070",
  accentDark: "#006557",
  /** Verdigris lifted for the dark surfaces. */
  mint: "#2FC7AA",
  popover: "#0B0F19",
  popoverText: "#F4F4F5",
  /** Body / UI text. */
  sans: INTER_STACK,
  /** Headlines and price figures — same family, heavier weight at the call site. */
  display: INTER_STACK,
  /** Uppercase, letter-spaced eyebrows. */
  label: INTER_STACK,
} as const;

export const DEMO_HREF = "/demo";
export const LOGIN_HREF = "/login";

/** `t.rich` tags for section headlines; emphasis stays upright. */
export const HEADING_RICH_TAGS = {
  em: (chunks: ReactNode): ReactNode => (
    <em style={{ fontStyle: "normal" }}>{chunks}</em>
  ),
};

export function heroTagline(t: Translate) {
  return t("jedesModulJederMitarbeiter");
}

export function includedIntro(t: Translate) {
  return t("keineExtrasNichtsFreischalten");
}

export function hiddenCostsLead(t: Translate) {
  return t("k49CentPreisSteckt");
}

export function supportFeeTitle(t: Translate) {
  return t("grundgebuehr");
}

export function supportFeeSub(t: Translate) {
  return t("fuerMeistenVerwaltungenInklusive");
}

/** Brand lockup: dark rounded tile + white play-triangle + wordmark. */
export function BrandLogo({
  size = 30,
}: { readonly size?: number }): ReactNode {
  const t = useTranslations("public.pricingShared");
  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: 10 }}
      aria-label={BRAND_LOGO_ALT}
      title={BRAND_LOGO_TITLE}
    >
      <BrandLogoMark width={size} height={size} variant="dark" />
      <span
        style={{
          fontSize: size >= 30 ? 18 : 17,
          fontWeight: 700,
          letterSpacing: "-0.01em",
          color: T.ink,
          fontFamily: T.sans,
        }}
      >
        {t.rich("hausverwalterAi", {
          suffix: (chunks) => (
            <span style={{ color: "#8A8D94" }}>{chunks}</span>
          ),
        })}
      </span>
    </span>
  );
}

/** Verdigris infinity glyph used in the "Unbegrenzt" highlight. */
export function InfinityIcon({
  size,
  strokeWidth = 2,
}: {
  readonly size: number;
  readonly strokeWidth?: number;
}): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={T.accent}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block", flex: "none" }}
      aria-hidden="true"
    >
      <path d="M12 12c-2-2.67-4-4-6-4a4 4 0 1 0 0 8c2 0 4-1.33 6-4Zm0 0c2 2.67 4 4 6 4a4 4 0 0 0 0-8c-2 0-4 1.33-6 4Z" />
    </svg>
  );
}

/** Green check used in every "included" list item. */
export function CheckMark({
  size = 15,
  color = T.accent,
  strokeWidth = 2.6,
}: {
  readonly size?: number;
  readonly color?: string;
  readonly strokeWidth?: number;
}): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block", flex: "none" }}
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export type IncludedItem = {
  readonly name: string;
  readonly bald?: boolean;
};

export type IncludedCategory = {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly sub: string;
  readonly count: string;
  readonly items: readonly IncludedItem[];
};

export const BASE_FEE_ICON: LucideIcon = Receipt;

/** Hero "everything-included" pills, verbatim order from the prototype. */
export function heroPills(t: Translate): readonly string[] {
  return [
    "Inbox",
    t("dokumente"),
    t("telefonanlage"),
    "Kundenportal",
    "Mailings",
    "Team-Chat",
    t("stammdaten"),
    t("aufgaben"),
    t("terminbuchung"),
    t("kiAnrufbeantworter"),
    t("uebergabeformulare"),
    "Online-Meetings",
    t("wissen"),
    t("kiAgenten"),
    t("kopilotKi"),
    t("kalender"),
    "Online-ETV",
    t("mobilversion"),
    t("buchhaltung"),
  ];
}

/** Same chip sizing as the marketing homepage feature row — keeps 19 pills on two lines. */
export const HERO_PILL_ROW_STYLE: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  justifyContent: "center",
  maxWidth: 1040,
  margin: "0 auto 34px",
};

export const HERO_PILL_STYLE: CSSProperties = {
  fontFamily: T.sans,
  fontSize: 12.5,
  fontWeight: 500,
  padding: "7px 13px",
  borderRadius: 999,
  background: "#fff",
  color: T.ink,
  border: "1px solid #E7E9ED",
  boxShadow: "0 1px 2px rgba(11,15,25,0.05)",
};

export function PricingHeroPill(props: { readonly label: string }): ReactNode {
  return <span style={HERO_PILL_STYLE}>{props.label}</span>;
}

export function unlimitedRows(t: Translate): readonly {
  readonly label: string;
  readonly value: string;
}[] {
  return [
    { label: t("mitarbeiterzugaenge"), value: t("jedesTeammitglied") },
    { label: t("portalzugaenge"), value: t("eigentuemerMieter") },
    { label: t("objekteKontakte"), value: t("einheitenAdressen") },
  ];
}

export function includedCategories(t: Translate): readonly IncludedCategory[] {
  return [
    {
      icon: Inbox,
      title: t("arbeitsplatzVorgaenge"),
      sub: t("allesTaeglicheOrt"),
      count: "11 Module",
      items: [
        { name: t("vorgangsmanagementSystem") },
        { name: t("dokumentenmanagementSystem") },
        { name: t("kundenportal") },
        { name: t("teamchatModul") },
        { name: t("telefonie") },
        { name: t("kalender") },
        { name: t("terminbuchung") },
        { name: t("onlineMeetings") },
        { name: t("mobilversion") },
        { name: t("projektmanagementSystem") },
        { name: t("formularModul") },
      ],
    },
    {
      icon: Building2,
      title: t("verwaltungsModule"),
      sub: t("speziellFuerHausverwaltung"),
      count: "5 Module",
      items: [
        { name: t("mailingsModul") },
        { name: t("onlineHybridEigentuemerversammlungen") },
        { name: t("mieterhoehungen") },
        { name: t("wartungen") },
        { name: t("zaehlerstaende") },
      ],
    },
    {
      icon: Bot,
      title: t("kiAgenten"),
      sub: t("arbeitetNicht"),
      count: "5 Module",
      items: [
        { name: t("autonomeKiAgenten") },
        { name: t("coPilotKi") },
        { name: t("wissensbereich") },
        { name: t("kiAnrufbeantworter") },
        { name: t("voiceAgenten"), bald: true },
      ],
    },
    {
      icon: Cable,
      title: t("schnittstellen"),
      sub: t("deinenSystemenVerbunden"),
      count: "5 Module",
      items: [
        { name: t("stammdatenSynchronisierungDeinemErp") },
        { name: t("apiSchnittstelle") },
        {
          name: t("optionalSynchronisierungExVorgangssystem"),
        },
        { name: t("whatsappIntegration") },
        { name: t("eMailPostfaecherVerbinden") },
      ],
    },
    {
      icon: Truck,
      title: t("umzugshilfe"),
      sub: t("begleitenWechsel"),
      count: "4 Module",
      items: [
        { name: t("datenuebernahmeBegleitung") },
        { name: t("eMailImport") },
        { name: t("dokumentenImport") },
        { name: t("ticketImport") },
      ],
    },
  ];
}
